<script setup lang="ts">
interface GdprConsents {
  acceptResponsibility: boolean;
  agreePrivacy: boolean;
  allConsented: boolean;
  over16: boolean;
}

type GdprConsentKey = keyof GdprConsents;

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const STEP_KEYS: readonly string[] = [
  'PhotoFromPhone.step1',
  'PhotoFromPhone.step2',
  'PhotoFromPhone.step3',
];

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const CONSENT_CHECKBOXES: readonly (readonly [GdprConsentKey, string])[] = [
  ['allConsented', 'PhotoFromPhone.gdprConsentAll'],
  ['over16', 'PhotoFromPhone.gdprOver16'],
  ['acceptResponsibility', 'PhotoFromPhone.gdprResponsibility'],
];

interface Props {
  gdprConsents: Readonly<GdprConsents>;
  hasUploadedAndAnimatedPhotos: boolean;
  showGdprCheckboxes: boolean;
}

interface Emits {
  gdprConsentChange: [key: GdprConsentKey, value: boolean];
  privacyPolicyClick: [];
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onGdprConsentChange(key: GdprConsentKey, event: Event): void {
  emit(
    'gdprConsentChange',
    key,
    (event.currentTarget as HTMLInputElement).checked
  );
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onPrivacyPolicyClick(): void {
  emit('privacyPolicyClick');
}
</script>

<template>
  <div class="PhotoFromPhone__content kiosk-caption">
    <h1 class="PhotoFromPhone__title kiosk-title">
      {{ $t('PhotoFromPhone.title') }}
    </h1>

    <ol class="PhotoFromPhone__list">
      <li
        v-for="stepKey in STEP_KEYS"
        :key="stepKey"
        class="PhotoFromPhone__list-item"
      >
        {{ $t(stepKey) }}
      </li>
    </ol>

    <div
      v-if="showGdprCheckboxes"
      class="PhotoFromPhone__gdpr-checkboxes"
    >
      <label
        v-for="[consentKey, labelKey] in CONSENT_CHECKBOXES"
        :key="consentKey"
        class="PhotoFromPhone__checkbox"
      >
        <input
          type="checkbox"
          data-testid="photo-phone-checkbox"
          :checked="gdprConsents[consentKey]"
          @change="onGdprConsentChange(consentKey, $event)"
        />
        <span>{{ $t(labelKey) }}</span>
      </label>

      <label class="PhotoFromPhone__checkbox">
        <input
          type="checkbox"
          data-testid="photo-phone-checkbox"
          :checked="gdprConsents.agreePrivacy"
          @change="onGdprConsentChange('agreePrivacy', $event)"
        />
        <span>
          {{ $t('PhotoFromPhone.gdprAgreeToPrefix') }}
          <a
            href="#"
            class="PhotoFromPhone__privacy-link"
            data-testid="photo-phone-privacy-link"
            @click.prevent.stop="onPrivacyPolicyClick"
          >
            {{ $t('PhotoFromPhone.gdprPrivacyPolicyLink') }}
          </a>
          {{ $t('PhotoFromPhone.gdprAgreeToSuffix') }}
        </span>
      </label>
    </div>

    <div
      v-show="!hasUploadedAndAnimatedPhotos"
      class="PhotoFromPhone__wait kiosk-uppercase"
      data-testid="photo-phone-waiting"
    >
      <i class="PhotoFromPhone__wait-spinner kiosk-spinner" />
      {{ $t('PhotoFromPhone.waiting') }}
    </div>

    <div
      v-show="hasUploadedAndAnimatedPhotos"
      class="PhotoFromPhone__success kiosk-uppercase"
      data-testid="photo-phone-success"
    >
      <i class="PhotoFromPhone__success-icon kiosk-success-icon" />
      {{ $t('PhotoFromPhone.done') }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.PhotoFromPhone__content {
  width: var(--photo-from-phone-content-width, 32rem);
}

.PhotoFromPhone__title {
  padding: var(--photo-from-phone-title-padding, 0);
  margin-bottom: var(--photo-from-phone-title-margin-bottom, 1.6rem);
  text-align: left;
}

.PhotoFromPhone__list {
  padding: 0;
  margin: 0;
  list-style: none;
  counter-reset: PhotoFromPhone__list;
}

.PhotoFromPhone__list-item {
  position: relative;
  padding: 0.3rem 0 0.3rem 1.8rem;
  margin: 0;
  counter-increment: PhotoFromPhone__list;
}

.PhotoFromPhone__list-item::before {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0.3rem 0;
  content: counter(PhotoFromPhone__list) '.';
}

.PhotoFromPhone__gdpr-checkboxes {
  padding: var(--photo-from-phone-gdpr-padding, 1.5rem);
  margin-top: var(--photo-from-phone-gdpr-margin-top, 1rem);
  text-align: left;
  background: rgba(var(--Kiosk--font_color_rgb, 0, 0, 0), 0.05);
  border-radius: 0.5rem;
}

.PhotoFromPhone__checkbox {
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  margin: var(--photo-from-phone-checkbox-margin, 1rem) 0;
  font-size: var(--photo-from-phone-checkbox-font-size, 1.1rem);
  cursor: pointer;
}

.PhotoFromPhone__checkbox input[type='checkbox'] {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  margin-top: 0.2rem;
  cursor: pointer;
}

.PhotoFromPhone__checkbox span {
  flex: 1;
  line-height: 1.6;
}

.PhotoFromPhone__privacy-link {
  font-weight: bold;
  color: var(--Kiosk--font_color);
  text-decoration: underline;
}

.PhotoFromPhone__privacy-link:hover {
  opacity: 0.8;
}

.PhotoFromPhone__wait {
  margin-top: 2rem;
  opacity: 0.7;
}

.PhotoFromPhone__wait-spinner {
  --kiosk-spinner-size: 1.1rem;
  --kiosk-spinner-color: var(--Kiosk--font_color);
  --kiosk-spinner-opacity: 1;
  --kiosk-spinner-thinkness: 0.25;

  display: inline-block;
  margin: 0 0.7rem 0 0.2rem;
}

.PhotoFromPhone__success {
  position: relative;
  padding-left: 3rem;
  margin-top: 2rem;
}

.PhotoFromPhone__success-icon {
  --kiosk-success-icon-size: 2.2rem;

  position: absolute;
  top: -0.25rem;
  left: 0;
}
</style>
